import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MessageCircle, Send, CheckCircle, XCircle, RefreshCw, 
  Phone, Clock, AlertTriangle, Activity, Settings, ArrowLeft
} from 'lucide-react';
import { LOGO_DARK_BG } from '../../config/branding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminWhatsAppPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const adminToken = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!adminToken) {
      navigate('/admin/login');
      return;
    }
  }, [adminToken, navigate]);

  const loadData = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${adminToken}` };
      
      const [statusRes, statsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/whatsapp/status`, { headers }),
        fetch(`${API_URL}/api/admin/whatsapp/stats`, { headers }),
        fetch(`${API_URL}/api/admin/whatsapp/logs?limit=20`, { headers })
      ]);

      if (statusRes.ok) setStatus(await statusRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (logsRes.ok) {
        const data = await logsRes.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error loading WhatsApp data:', error);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sendTestMessage = async () => {
    if (!testPhone) return;
    
    setSending(true);
    setTestResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/whatsapp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: testPhone,
          message: testMessage || undefined
        })
      });
      
      const result = await response.json();
      setTestResult(result);
      
      if (result.success) {
        loadData();
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img src={LOGO_DARK_BG} alt="Logo" className="h-8" />
            <div className="h-6 w-px bg-slate-700" />
            <h1 className="text-xl font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-green-400" />
              WhatsApp Integration
            </h1>
          </div>
          <button
            onClick={loadData}
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            title="Aktualisieren"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-6xl mx-auto p-6">
        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Connection Status */}
          <div className={`bg-white rounded-xl p-6 shadow-sm border-l-4 ${
            status?.success ? 'border-green-500' : 'border-red-500'
          }`}>
            <div className="flex items-center gap-3 mb-2">
              {status?.success ? (
                <CheckCircle className="w-6 h-6 text-green-500" />
              ) : (
                <XCircle className="w-6 h-6 text-red-500" />
              )}
              <span className="font-semibold text-gray-700">Verbindung</span>
            </div>
            <p className={`text-lg font-bold ${status?.success ? 'text-green-600' : 'text-red-600'}`}>
              {status?.success ? 'Aktiv' : 'Getrennt'}
            </p>
            {status?.account_name && (
              <p className="text-sm text-gray-500 mt-1">{status.account_name}</p>
            )}
          </div>

          {/* Total Messages */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Send className="w-6 h-6 text-blue-500" />
              <span className="font-semibold text-gray-700">Gesamt</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats?.total || 0}</p>
            <p className="text-sm text-gray-500">Nachrichten</p>
          </div>

          {/* Success Rate */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-6 h-6 text-green-500" />
              <span className="font-semibold text-gray-700">Erfolgsrate</span>
            </div>
            <p className="text-2xl font-bold text-green-600">{stats?.success_rate || 0}%</p>
            <p className="text-sm text-gray-500">{stats?.successful || 0} erfolgreich</p>
          </div>

          {/* Last 24h */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-6 h-6 text-purple-500" />
              <span className="font-semibold text-gray-700">Letzte 24h</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats?.last_24_hours || 0}</p>
            <p className="text-sm text-gray-500">Nachrichten</p>
          </div>
        </div>

        {/* Test & Info Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Test Message */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600" />
              Test-Nachricht senden
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefonnummer
                </label>
                <div className="flex gap-2">
                  <Phone className="w-5 h-5 text-gray-400 mt-2.5" />
                  <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    placeholder="+49 170 1234567"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nachricht (optional)
                </label>
                <textarea
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  placeholder="Standard Test-Nachricht wird verwendet..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              
              <button
                onClick={sendTestMessage}
                disabled={!testPhone || sending}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-semibold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Sende...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Test senden
                  </>
                )}
              </button>
              
              {testResult && (
                <div className={`p-4 rounded-lg ${
                  testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  {testResult.success ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-green-800">Erfolgreich gesendet!</p>
                        <p className="text-sm text-green-600">SID: {testResult.message_sid}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">Fehler beim Senden</p>
                        <p className="text-sm text-red-600">{testResult.error}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sandbox Info */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-green-600" />
              Twilio Sandbox Setup
            </h2>
            
            <div className="bg-white/70 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-800">Wichtig für Tests:</p>
                  <p className="text-sm text-gray-600 mt-1">
                    Empfänger müssen zuerst dem Sandbox beitreten, bevor sie Nachrichten erhalten können.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <span>WhatsApp öffnen</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <span>Nachricht an <strong>+1 415 523 8886</strong> senden</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <span>Text: <code className="bg-gray-100 px-2 py-0.5 rounded">join eaten-range</code></span>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-green-200">
              <p className="text-xs text-gray-500">
                Für Produktions-WhatsApp muss eine echte WhatsApp Business Nummer bei Twilio registriert werden.
              </p>
            </div>
          </div>
        </div>

        {/* Recent Logs */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Letzte Nachrichten</h2>
          </div>
          
          {logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Noch keine WhatsApp-Nachrichten gesendet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {logs.map((log, index) => (
                <div key={index} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {log.success ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{log.phone}</p>
                        <p className="text-sm text-gray-500">
                          Fall: {log.case_id === 'TEST' ? 'Test-Nachricht' : log.case_id?.slice(0, 8)}
                          {' · '}Status: {log.status}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {new Date(log.timestamp).toLocaleString('de-DE')}
                      </p>
                      {log.error && (
                        <p className="text-xs text-red-500 mt-1">{log.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminWhatsAppPage;
